import { useState, useEffect, useCallback } from 'react';

import type {
  OfflineStoreData,
  StoreData,
} from '~domains/partner-application/entities/application';

import { useAddressCoordinates } from '~entities/address-suggestion';

import { Loader } from '~shared/ui/loader';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Button } from '@breezeventures/ui-kit/button';
import { Map, type LngLat } from '@breezeventures/ui-kit/map';
import { Typography } from '@breezeventures/ui-kit/typography';

type StoreMapProps = {
  storeDraft?: Partial<StoreData> | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (data: { coordinates: LngLat | null }) => void;
};

export const StoreMap = ({
  storeDraft,
  onBack,
  onClose,
  onSubmit,
}: StoreMapProps) => {
  const offlineStoreDraft = storeDraft as OfflineStoreData | null;
  const address = offlineStoreDraft?.address ?? {};
  const initialCoordinates = offlineStoreDraft?.coordinates ?? null;

  const coordinatesQuery = useAddressCoordinates({
    params: {
      ...address,
      coordinatesRequired: true,
    },
    enabled: !initialCoordinates,
  });

  const [coordinates, setCoordinates] = useState<LngLat | null>(
    initialCoordinates || null
  );
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  useEffect(() => {
    if (coordinatesQuery.data && !initialCoordinates) {
      setCoordinates(coordinatesQuery.data);
    }
  }, [coordinatesQuery.data, initialCoordinates]);

  const onCoordinatesChange = useCallback((lngLat: LngLat) => {
    setCoordinates(lngLat);
  }, []);

  const onMapLoad = useCallback(() => {
    setIsMapLoaded(true);
  }, []);

  const handleCantFind = useCallback(() => {
    onSubmit({ coordinates: null });
  }, [onSubmit]);

  const onMapLoadError = useCallback(() => {
    handleCantFind();
  }, [handleCantFind]);

  const handleConfirm = useCallback(() => {
    if (coordinates) {
      onSubmit({ coordinates });
    }
  }, [coordinates, onSubmit]);

  const isCoordinatesNotFound =
    !initialCoordinates &&
    !coordinatesQuery.data &&
    !coordinatesQuery.isLoading;

  if (coordinatesQuery.isError || isCoordinatesNotFound) {
    return (
      <Screen header={<ScreenHeader onBack={onBack} onClose={onClose} />}>
        <div
          className={`
            flex flex-1 flex-col items-center justify-center p-4 pt-20
          `}
        >
          <Typography.Title size="s" className="mb-4 text-center">
            Unable to load map
          </Typography.Title>
          <Typography.Text size="m" className="mb-6 text-center">
            We couldn&apos;t find the location for your address
          </Typography.Text>
          <Button onClick={handleCantFind} variant="secondary" size="l">
            Next
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      bodyClassName="relative"
      header={<ScreenHeader onBack={onBack} onClose={onClose} />}
    >
      <div className="absolute inset-0">
        {(!isMapLoaded || coordinatesQuery.isLoading) && (
          <div
            className={`relative flex h-full w-full items-center justify-center`}
          >
            <Loader />
          </div>
        )}
        {!!coordinates && (
          <div className="h-full w-full">
            <Map
              size="full"
              onClick={onCoordinatesChange}
              onLoad={onMapLoad}
              onError={onMapLoadError}
              initialCenterCoordinates={coordinates}
            />
          </div>
        )}
      </div>

      <div
        className={`
          absolute right-0 bottom-0 left-0 z-20 flex w-full flex-col gap-3
          rounded-t-xl bg-white p-4
        `}
      >
        <div className="mb-4 flex flex-col gap-2 pt-6">
          <Typography.Title size="s">
            Pin your store&apos;s exact location on the&nbsp;map
          </Typography.Title>
          <Typography.Text
            size="s"
            className="text-[var(--color-base-primary)]"
          >
            Make sure the pin matches the store address you entered.
          </Typography.Text>
        </div>
        <Button
          onClick={handleConfirm}
          variant="primary"
          size="l"
          disabled={!coordinates}
        >
          Confirm place
        </Button>
        <Button variant="ghost" size="l" onClick={handleCantFind}>
          Can&apos;t find my store
        </Button>
      </div>
    </Screen>
  );
};
